import React, { useRef, useState, useCallback, useEffect } from "react";
import { observer } from "mobx-react";
import announcementStore from "../../stores/AnnouncementStore";
import { getAnnouncementColors } from "./announcementColors";

const MIN_W = 280;
const MIN_H = 140;
const TEMP_HIDE_DELAY = 10 * 60 * 1000; // 10 minutos

const AnnouncementPopup = observer(({ item }) => {
  const { announcementId, title, message, dismissable, type } = item;
  const ui = announcementStore.ui.get(announcementId);
  const colors = getAnnouncementColors(type);

  const dragRafRef = useRef(null);
  const resizeRafRef = useRef(null);
  const draggingRef = useRef(false);
  const resizingRef = useRef(false);
  const cleanupRef = useRef(null);

  // Estado para esconder temporariamente (quando dismissable === false)
  const [tempHidden, setTempHidden] = useState(false);
  const tempTimerRef = useRef(null);

  // Limpa listeners e timers ao desmontar
  useEffect(function () {
    return function () {
      if (cleanupRef.current) cleanupRef.current();
      if (dragRafRef.current) cancelAnimationFrame(dragRafRef.current);
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
      if (tempTimerRef.current) clearTimeout(tempTimerRef.current);
    };
  }, []);

  const onDragStart = useCallback(
    function (e) {
      if (draggingRef.current || resizingRef.current) return;
      e.preventDefault();
      draggingRef.current = true;

      announcementStore.bringToFront(announcementId);

      var uiState = announcementStore.ui.get(announcementId);
      if (!uiState) { draggingRef.current = false; return; }

      var startX = e.clientX;
      var startY = e.clientY;
      var origX = uiState.x;
      var origY = uiState.y;

      e.target.setPointerCapture(e.pointerId);

      function onMove(ev) {
        if (dragRafRef.current) cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = requestAnimationFrame(function () {
          dragRafRef.current = null;
          announcementStore.setRect(announcementId, {
            x: origX + (ev.clientX - startX),
            y: origY + (ev.clientY - startY),
          });
        });
      }

      function onUp(ev) {
        draggingRef.current = false;
        cleanupRef.current = null;
        try { ev.target.releasePointerCapture(ev.pointerId); } catch (_) {}
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        if (dragRafRef.current) {
          cancelAnimationFrame(dragRafRef.current);
          dragRafRef.current = null;
        }
      }

      // Limpa listener anterior caso tenha ficado preso
      if (cleanupRef.current) cleanupRef.current();
      cleanupRef.current = function () {
        draggingRef.current = false;
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        if (dragRafRef.current) {
          cancelAnimationFrame(dragRafRef.current);
          dragRafRef.current = null;
        }
      };

      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    },
    [announcementId]
  );

  const onResizeStart = useCallback(
    function (e) {
      if (draggingRef.current || resizingRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      resizingRef.current = true;

      announcementStore.bringToFront(announcementId);

      var uiState = announcementStore.ui.get(announcementId);
      if (!uiState) { resizingRef.current = false; return; }

      var startX = e.clientX;
      var startY = e.clientY;
      var origW = uiState.w;
      var origH = uiState.h;

      e.target.setPointerCapture(e.pointerId);

      function onMove(ev) {
        if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = requestAnimationFrame(function () {
          resizeRafRef.current = null;
          announcementStore.setRect(announcementId, {
            w: Math.max(MIN_W, origW + (ev.clientX - startX)),
            h: Math.max(MIN_H, origH + (ev.clientY - startY)),
          });
        });
      }

      function onUp(ev) {
        resizingRef.current = false;
        cleanupRef.current = null;
        try { ev.target.releasePointerCapture(ev.pointerId); } catch (_) {}
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        if (resizeRafRef.current) {
          cancelAnimationFrame(resizeRafRef.current);
          resizeRafRef.current = null;
        }
      }

      if (cleanupRef.current) cleanupRef.current();
      cleanupRef.current = function () {
        resizingRef.current = false;
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        if (resizeRafRef.current) {
          cancelAnimationFrame(resizeRafRef.current);
          resizeRafRef.current = null;
        }
      };

      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    },
    [announcementId]
  );

  var handleCloseClick = useCallback(function (e) {
    e.stopPropagation();
    if (dismissable) {
      // Permanente
      announcementStore.dismiss(announcementId);
    } else {
      // Temporário: esconde e reabre em 5 minutos
      setTempHidden(true);
      if (tempTimerRef.current) clearTimeout(tempTimerRef.current);
      tempTimerRef.current = setTimeout(function () {
        tempTimerRef.current = null;
        setTempHidden(false);
      }, TEMP_HIDE_DELAY);
    }
  }, [announcementId, dismissable]);

  if (!ui || tempHidden) return null;

  return (
    <div
      role="dialog"
      aria-label={title || "Aviso"}
      style={{
        position: "absolute",
        left: ui.x,
        top: ui.y,
        width: ui.w,
        height: ui.h,
        zIndex: ui.z,
        pointerEvents: "auto",
        touchAction: "none",
        display: "flex",
        flexDirection: "column",
        borderRadius: 8,
        border: `2px solid ${colors.borderColor}`,
        boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
        overflow: "hidden",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        userSelect: "none",
      }}
      onPointerDown={() => announcementStore.bringToFront(announcementId)}
    >
      {/* Header */}
      <div
        onPointerDown={onDragStart}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          background: colors.headerBg,
          color: colors.headerText,
          cursor: "grab",
          touchAction: "none",
          flexShrink: 0,
          minHeight: 36,
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 16,
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ fontSize: 18 }}>{colors.icon}</span>
          {title}
        </span>

        <button
          onClick={handleCloseClick}
          aria-label="Fechar aviso"
          style={{
            background: "transparent",
            border: "none",
            color: colors.headerText,
            cursor: "pointer",
            fontSize: 18,
            lineHeight: 1,
            padding: "0 2px",
            marginLeft: 8,
            fontWeight: 700,
            opacity: 0.85,
            flexShrink: 0,
          }}
        >
          ✕
        </button>
      </div>

      {/* Body */}
      <div
        style={{
          flex: 1,
          padding: "10px 12px",
          background: colors.bodyBg,
          color: "#212121",
          fontSize: 15,
          lineHeight: 1.5,
          overflowY: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {message}
      </div>

      {/* Resize handle */}
      <div
        onPointerDown={onResizeStart}
        style={{
          position: "absolute",
          right: 0,
          bottom: 0,
          width: 24,
          height: 24,
          cursor: "nwse-resize",
          touchAction: "none",
          background: "transparent",
        }}
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          style={{ position: "absolute", right: 3, bottom: 3, opacity: 0.35 }}
        >
          <path d="M9 1L1 9M9 5L5 9M9 9L9 9" stroke="#333" strokeWidth="1.5" />
        </svg>
      </div>
    </div>
  );
});

export default AnnouncementPopup;
